/**
 * 이상한 방 - 매치 투영 (`MatchState` → 한 사람이 받아도 되는 것)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것) · 2.1절(`placedBy`는 서버 전용)
 *            `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.4절(원래 방)
 *            로드맵 모델 2.3절(투영 - 무엇을 보여 주는가는 함수 하나가 정한다) · 2.5절(뷰에서 규칙을 유도하지 않는다)
 *
 * ## 무엇을 벗기는가가 전부 여기 있다
 *
 * 술래에게 가는 뷰에서 **정답표(`changes`)는 수색 중 `null`**이고 리빌부터 전부다. 바꾸기 작업대의 `draft`는
 * 체인저 본인에게도 뷰로는 가지 않는다(방 데이터는 뷰 밖 `rooms` 메시지다) - 뷰에는 "지금 무엇을 보여 줄 수
 * 있는가"(`rooms`)만 있어 호스트가 그 플래그로 `rooms`를 보낸다. `ObjectInstance.placedBy`는 방 데이터에만 있고
 * 뷰는 방을 싣지 않으므로 `JSON.stringify(view)`에 그 키가 나올 길이 없다(`matchView.test.ts`가 문자열로 검사한다).
 *
 * `changes: null`은 정답표를 접시에 담아 주지 않는 것이지 비밀이 아니다(11.2절 - 두 방을 가진 클라이언트는
 * 차집합을 만들 수 있다). 그래서 벗기는 기준은 "치팅 방지"가 아니라 **"클라이언트가 규칙을 유도하지 못하게"**다.
 *
 * ## 불리언은 여기서 계산해 내린다 - 클라이언트는 셀렉터를 부르지 않는다
 *
 * `canShoot`·`canUseMagazine`·`canEnterOrigin`·`canStart`·`canChange`·`canRaise`·`hunting`·`spectatable`은 리듀서의 게이트(`matchShoot.isActiveHunter`·`isShotBlocked`·
 * `canReload`·`canRaise`·`canSpectate`, `matchOrigin.canEnterOrigin`, `matchState.canStart`, `matchChange.canUseDesk`)를 **그대로 호출**한 값이다. 이 파일에 `ammo > 0` 같은 비교식이
 * 보이면 그건 규칙이 새어 나온 것이다 - `sessionView.ts` 머리말과 같은 규약이고, 뷰가 하나라도 다시 계산하면
 * 버튼의 얼굴과 실제 동작이 튜닝 한 번에 갈린다.
 *
 * ## 직렬화 가능해야 한다
 *
 * 이 값은 와이어로 나간다(`ServerMessage.step.view`). `Map`·`Set`·함수가 들어가면 `JSON.stringify`가 조용히
 * `{}`로 바꾼다. 그래서 `RoomState`(Map)는 애초에 뷰에 없고, `verbCounts`는 `Record`다. `door.queue`는 id가
 * 아니라 **이름**이다 - 줄서기 연출("빨리 나와!!")이 필요한 것은 이름이고, id는 클라이언트가 다시 조회해야 한다.
 *
 * 이 파일은 `core`·`catalog`·`game` 리프만 본다(`boundary.test.ts`). 원래 방 버튼의 얼굴(`OriginRoomStatus`)은
 * `game/originVisit.ts`의 것이라 싱글 뷰(`sessionView`)·HUD·이 파일이 **같은 타입과 같은 함수**를 쓴다 - 사본이 없다.
 */

import { VERBS, type InstanceId, type PlayerId, type Verb } from '../../core/types'
import { comboMultiplier, isComboAtCap } from '../combo'
import { ammoCapacity, isLowAmmo, magazineUsesLeft } from '../gameState'
import { originStatusOf as visitStatusOf, originStayRemainingMs, type OriginRoomStatus } from '../originVisit'
import type { Tuning } from '../tuning'
import { canDecorate, placementsLeftOf } from './matchDecorate'
import { budgetLeftOf, canUseDesk, deskDone } from './matchDesk'
import { canEnterOrigin, graceMs } from './matchOrigin'
import { budgetPerPlayer, phaseRemainingMs, totalChangeCount } from './matchPhases'
import { cumulativeRanking } from './matchScore'
import {
  canSkipPhase,
  findableChanges,
  findableFound,
  foundBy,
  matchZones,
  playerOf,
  seatsInOrder,
  skipVotesNeeded,
  unfoundOf,
} from './matchSelectors'
import { canRaise, canReload, canSpectate, isActiveHunter, isShotBlocked } from './matchShoot'
import { canStart } from './matchState'
import type {
  ChangerWager,
  MatchChange,
  MatchDeps,
  MatchEndReason,
  MatchFound,
  MatchPhase,
  MatchPlayer,
  MatchState,
  RankingRow,
  SeatKind,
} from './matchTypes'
import { zoneOf, type ZoneAssignment } from './matchZones'

/* --- 뷰 타입 --------------------------------------------------------------------- */

/** 남에게 보여도 되는 좌석 정보. 잔탄·콤보·탄창은 없다 - 그건 자기 것(`SelfView`)이다. */
export type PublicPlayer = Readonly<{
  id: PlayerId
  name: string
  kind: SeatKind
  connected: boolean
  ready: boolean
  spectating: boolean
  /** 원래 방 안에 있는가. 문 연출("OO이 들어가 있다")의 재료다. */
  inOrigin: boolean
  foundCount: number
  roundScore: number
  totalScore: number
  raised: boolean
  /**
   * 바꾸기 중 이 사람이 지금까지 만든 개수(M11 규칙 3 "남의 진행은 예산 점 개수만"). 무엇을 건드렸는지는 없다 -
   * 그 목록(`DeskView.touched`)은 본인 뷰에만 있다. 바꾸기 밖에서는 0이다.
   */
  changeUsed: number
  /**
   * 더 기다릴 필요가 없는가. [다 했다]를 누른 사람이거나 **이웃·끊긴 좌석**이다(M11 규칙 7 - 이웃은 즉시 완료).
   * 리듀서의 마감 판정(`matchDesk.deskDone`)과 같은 함수라 대기 화면이 리듀서보다 오래 기다리는 일이 없다.
   * 바꾸기 밖에서는 거짓이다.
   */
  changeDone: boolean
  /**
   * 내가 지금 이 사람의 시점을 고를 수 있는가(M5 규칙 2 - 관전자만, 관전 중이 아닌 술래만, 나 자신은 아니다).
   * `matchShoot.canSpectate` 그대로 - 시점 버튼 줄(`ui/spectateBar.ts`)이 누구를 세울지가 이 하나에서 갈리고, 클라이언트가
   * `spectating && role === 'hunter'`를 다시 적지 않는다. 받는 사람에 따라 값이 다른 유일한 공개 필드다.
   */
  spectatable: boolean
}>

/** 나 자신의 것. `GameHudView`의 자원 필드와 이름이 같다 - HUD가 두 뷰를 같은 손으로 그린다. */
export type SelfView = Readonly<{
  ammo: number
  ammoMax: number
  isLowAmmo: boolean
  magazines: number
  magazineUsesLeft: number
  canUseMagazine: boolean
  canShoot: boolean
  canEnterOrigin: boolean
  /** 로비 [시작]이 지금 통하는가(방장 · 로비 · 전원 준비). `matchState.canStart` 그대로 - 클라이언트가 `ready`를 다시 세지 않는다. */
  canStart: boolean
  /**
   * 지금 내 작업대를 만질 수 있는가(바꾸기 페이즈 · 아직 커밋 전). `matchDesk.canUseDesk` 그대로 - 도구를 세울지 ·
   * 기다리는 화면을 펼지가 이 하나에서 갈리고, 클라이언트가 `phase === 'change'`를 다시 적지 않는다.
   */
  canChange: boolean
  /**
   * 지금 방을 꾸밀 수 있는가(M10 - 꾸미기 페이즈 · 좌석이 있음). `matchDecorate.canDecorate` 그대로다 -
   * 도구를 세울지가 이 하나에서 갈리고 클라이언트가 `phase === 'decorate'`를 다시 적지 않는다(`canChange`와 같은 규약).
   */
  canDecorate: boolean
  /**
   * 이 라운드에 내가 더 놓을 수 있는 개수(M10). **좌석에 없다** - 방에서 세면 나오는 값이고
   * (`matchDecorate.placementsLeftOf` → `core/room.countByPlayer`), 그 함수가 `core/verbs.applyAdd`의 한도 검사와
   * 같은 것이라 화면의 잠금과 호스트의 판정이 갈릴 수 없다.
   */
  placementsLeft: number
  /**
   * 인당 한도(M10). 띠가 점을 이 수만큼 찍는다 - **호스트의 `config`에서 온다.** 클라이언트의 튜닝에서 읽으면
   * URL 하나(`?placements=`)로 두 값이 갈리고, 그때 화면은 "8개 중 3개 남음"인데 서버는 5개에서 잠근다.
   */
  placementsLimit: number
  combo: number
  comboMultiplier: number
  comboAtCap: boolean
  /** 원래 방 버튼의 얼굴. `game/originVisit.OriginRoomStatus` - HUD가 싱글 뷰와 같은 손으로 그린다. */
  origin: OriginRoomStatus
  /** 줄에 섰으면 1부터 세는 내 자리. 아니면 `null`. */
  queuePosition: number | null
  spectating: boolean
  spectateTarget: PlayerId | null
  /**
   * 수색 중이고 쏘는 좌석인가(관전 아님). `matchShoot.isActiveHunter` 그대로 - HUD가 찾는 사람의 도구(걸기 칩)를 세울지가
   * 이 하나에서 갈린다. `canRaise`·`canShoot`은 이것 위에 잔탄 조건을 얹은 값이라 "왜 안 되는가"를 가르지 못한다 - 관전자에게
   * `canRaise` 거짓만 내리면 칩이 "탄환이 모자라다"고 거짓 이유를 말한다.
   */
  hunting: boolean
  /** 걸기(M7)를 선언했는가. HUD 배수 칩이 두 겹이 되는 근거다. */
  raised: boolean
  /**
   * 지금 걸 수 있는가(수색 · 술래 · 관전 아님 · 잔탄 · 아직 안 걸었다). `matchShoot.canRaise` 그대로 - 칩을 잠글지가
   * 이 하나에서 갈리고, 클라이언트가 `ammo >= 4`를 다시 적지 않는다(`canShoot`과 같은 규약).
   */
  canRaise: boolean
  /**
   * 내가 건 것("내 것 N개는 아무도 못 찾는다"). **본인에게만** - 남의 걸기는 뷰에 없다(알면 그 사람 구획을 먼저 뒤진다).
   * 좌석에 사는 이유는 작업대가 수색 진입에서 사라지기 때문이다. 리빌·결과까지 살아 있어 결과 화면이
   * 맞았는지 도장을 찍는 재료가 된다(맞았는지의 비교는 `raise.isWagerMet`).
   */
  wager: ChangerWager | null
  /**
   * **내 구획**에서 아직 아무도 못 찾은 수(`matchScore.zoneRoundScore`의 분자 - `matchSelectors.unfoundOf`). 결과 화면의
   * 머리글("아무도 못 본 N개")이 이 값을 그린다. 클라이언트가 `changes`에서 세지 않는 이유는 그 뺄셈이 점수의 것이고,
   * 화면이 다시 세면 호스트가 굳힌 점수와 갈리는 날이 오기 때문이다(`matchSessionView` 머리말 "산술이 없다").
   * 수색 중에도 참값이다 - 누가 내 것을 찾았는지는 `found`(주인 포함)에 이미 있어 감출 것이 없다.
   */
  unfoundCount: number
}>

/**
 * 구획 하나와 그 주인(`matchZones.ZoneAssignment` 그대로). **바꾸기 중에도 전원에게 간다**(M11 규칙 3) -
 * 누가 어디를 손대는지는 알아도 무엇을 했는지는 모른다. 이름은 여기 없다: 화면 문구는 `room.<zoneId>`가 사전에서
 * 가져오고(`game/reveal.zoneNameFor` 하나가 그 접두사의 원본), 리듀서가 문구 키를 만들면 그 키가 두 곳에 산다.
 */
export type ZoneView = ZoneAssignment

/**
 * 바꾸기 작업대의 얼굴. 체인저 본인은 무엇을 건드렸는지(`touched`)까지, 남은 개수만 본다 -
 * 남이 `touched`를 보면 수색 전에 정답표의 절반(대상 목록)을 손에 쥔다.
 * `budgetLeft`는 `matchChange.budgetLeftOf` 그대로다 - 도구는 여기에 답을 기다리는 요청만 얹고 `budget - used`를 다시 세지 않는다.
 */
export type DeskView = Readonly<{
  budget: number
  used: number
  budgetLeft: number
  touched: readonly InstanceId[] | null
}>

/**
 * 문 앞의 얼굴(로드맵 M5 규칙 5). **전원에게 같은 값**이 간다 - 보유자 · 그 사람의 남은 체류 · 줄(이름) · 예약자 · 예약 유예 잔여.
 * 남은 시간 둘은 리듀서의 리프(`originVisit.originStayRemainingMs`, `door.reservedRemainingMs`)가 낸 값 그대로다.
 * HUD가 "몇 초 남았나"를 계산하지 않는다 - 시계 보간(`matchClock`)이 `step` 이후 흐른 만큼 빼는 것까지가 클라이언트의 일이다.
 */
export type DoorView = Readonly<{
  holder: PlayerId | null
  /** 보유자의 남은 체류(ms). 보유자가 없으면 0. */
  holderRemainingMs: number
  /** 줄 선 사람들의 **이름**, 머리부터. */
  queue: readonly string[]
  reservedFor: PlayerId | null
  /** 예약 유예 잔여(ms). 예약자가 없으면 0. 내 차례 유예 게이지의 분자다. */
  reservedRemainingMs: number
  /**
   * 예약 유예 전체(ms) - 게이지의 분모. 호스트의 `doorReserveSeconds`다. 뷰에 싣는 이유는 분자와 **같은 출처**여야 하기 때문이다 -
   * 클라이언트가 자기 튜닝에서 분모를 만들면 서버(M6)에서 호스트와 `?reserve=`가 다른 날 게이지 비율만 거짓이 된다(`ammoMax`와 같은 결정).
   */
  reserveTotalMs: number
}>

/**
 * 호스트가 이 사람에게 어느 방을 보내도 되는가. 방 데이터는 뷰 밖(`rooms` 메시지)이지만 "보여 줄 수 있는가"는
 * 투영의 결정이라 여기 둔다. 바꾸기 중 `mutated`의 자리에 오는 것은 **그 사람의 초안**이다(로드맵 2.3절 · M11 규칙 3).
 */
export type RoomsVisibility = Readonly<{
  original: boolean
  mutated: 'mutated' | 'draft' | null
}>

/**
 * 방장이 로비에서 만지는 설정의 **지금 값**(로드맵 M6). 로비 화면이 토글의 얼굴을 그리는 재료다 - 클라이언트가 `defaultMatchConfig`를
 * 스스로 만들어 들고 있으면 방장이 `configure`를 보낸 뒤 남들의 화면이 옛 값을 보인다. `allowedVerbs`는 `Set`이 아니라 배열이다
 * (와이어 - 머리말 "직렬화 가능해야 한다"). 순서는 정본 `VERB_ORDER`라 JSON이 결정론적이다.
 */
export type MatchConfigView = Readonly<{
  allowedVerbs: readonly Verb[]
  /** **인당** 이상한 곳 수(M11). 라운드 총량이 아니다 - 총량은 좌석 수의 함수다(`matchPhases` 머리말). */
  changeCount: number
  /**
   * **아직 만지는 UI가 없다.** M6 로비의 손잡이는 앞의 셋뿐이고(`ui/lobbySeats.LobbySettingsView`), 이 값을 읽는 화면은 없다 -
   * 소비자를 찾다 헤매지 않도록 적어 둔다. 그럼에도 뷰에 있는 이유는 이것이 "모두에게 같은 정보"에 속하기 때문이다:
   * 개수 예고가 켜졌는지는 라운드가 시작되기 전에 알아야 하는 값이고(`ClientView.verbCounts`는 수색부터 온다),
   * 로비에 토글이 서는 날 클라이언트가 `defaultMatchConfig`를 스스로 만들어 들면 남들 화면이 옛 값을 보인다(위 주석).
   */
  revealVerbCounts: boolean
  /**
   * 이 판이 꾸미기 페이즈로 시작하는가(M10). 로비의 토글이 이 값을 읽고, 방장이 아닌 사람도 시작 전에 알아야 한다 -
   * 빈 방에 떨어질지 다 차린 방을 외울지는 "모두에게 같은 정보"이기 때문이다(`revealVerbCounts`와 같은 이유).
   */
  humanDecorate: boolean
}>

export type ClientView = Readonly<{
  me: PlayerId
  roomCode: string
  round: number
  phase: MatchPhase
  phaseRemainingMs: number | null
  hostId: PlayerId
  players: readonly PublicPlayer[]
  self: SelfView
  /**
   * 넘어가기 표(`matchPhases.onSkipVote`). 화면은 이 둘을 그대로 그린다 - "N/M"의 두 수를 세지 않는다.
   * `needed`는 사람 좌석 수라 `players`에서 유도할 수도 있지만, 그러면 정족수 규칙이 화면에 한 벌 더 생긴다.
   */
  skip: Readonly<{ votes: number; needed: number; mine: boolean }>
  /**
   * **내가 찾을 수 있는** 이상한 곳의 수 = 전체 − 내 구획(M11 규칙 4). HUD "찾음 N/M"의 분모이고 결과 화면도 같다.
   * 전체를 내리지 않는 이유는 그 수가 내 화면에서 뜻을 갖지 않기 때문이다 - 내 구획의 것은 내가 영원히 못 찾는다.
   */
  changeCount: number
  /** 그중 이미 찾힌 수(누가 찾았든). "찾음 N/M"의 분자 - 클라이언트가 `found`를 걸러 세지 않는다. */
  foundCount: number
  /**
   * 누가 어느 구획인가. 어느 페이즈에나 있다(로비는 빈 배열) - 바꾸기 중에도 공개다(`ZoneView` 주석).
   * 라운드마다 한 칸 돈다(`matchZones.assignZones`).
   *
   * **지금 이 배열을 그리는 화면은 없다.** 규칙층에 공개로 두는 것과 화면에 나란히 띄우는 것은 다른 결정이라
   * 그렇다 - 바꾸기 60초 동안 "누가 어느 구획"을 진행판 옆에 두면 그것이 "저 사람 구획부터 뒤지자"라는 화면의
   * 조언이 된다(`ui/zoneProgress.ts` 머리말). 화면이 읽는 것은 `myZone` 하나다. 지우지 마라 - 공개라는 사실이
   * 규칙이고(로드맵 M11), 관전·리빌이 "저기는 누구 구획이었나"를 그리기 시작하면 그때 읽는 값이 이것이다.
   */
  zones: readonly ZoneView[]
  /** 그중 내 것. 없으면 `null`(좌석보다 구획이 적은 판 - `matchZones` 머리말). 도구가 손댈 수 있는 사각형이 이것이다. */
  myZone: ZoneView | null
  /** 로비 설정의 지금 값. 어느 페이즈에나 있다 - 결과 화면 뒤 다음 라운드의 로비가 같은 값을 보여야 한다. */
  config: MatchConfigView
  found: readonly MatchFound[]
  /** 수색 중 `null`, 리빌부터 전부. 바꾸기 중에는 **내 작업대의 것**(내가 만든 것은 내가 안다). */
  changes: readonly MatchChange[] | null
  desk: DeskView | null
  door: DoorView
  rooms: RoomsVisibility
  /** `config.revealVerbCounts`일 때 수색부터. 아니면 `null`. */
  verbCounts: Readonly<Record<Verb, number>> | null
  /** 누적 순위. 결과에서만 - 라운드 순위는 `roundScored` 효과로 이미 갔다. */
  ranking: readonly RankingRow[] | null
  endReason: MatchEndReason | null
}>

/* --- 상수 ------------------------------------------------------------------------ */

/** 정답표가 술래에게 열리는 페이즈. 리빌이 정답을 보여 주는 자리이므로 그때부터다. */
const CHANGES_VISIBLE_TO_HUNTERS: ReadonlySet<MatchPhase> = new Set<MatchPhase>(['reveal', 'result'])

/** 변경본이 존재하는 페이즈. 외우기·바꾸기에는 `mutated`가 `original`과 같은 참조라 보낼 것이 없다. */
const MUTATED_EXISTS: ReadonlySet<MatchPhase> = new Set<MatchPhase>(['hunt', 'reveal', 'result'])

/**
 * `verbCounts`를 세는 순서 = 정본 `core/types.VERBS`. `Record`의 키 순서가 곧 JSON 순서라 결정론에 들어간다. 화면이 개수를 읊는
 * 순서(`matchSessionPhases.verbCountsText`)와 생성기·팔레트가 같은 배열을 보므로 여기서 리터럴을 다시 적지 않는다.
 */
export const VERB_ORDER: readonly Verb[] = VERBS

/** 줄 자리는 1부터 센다(`matchOrigin`과 같다). */
const FIRST_QUEUE_POSITION = 1

/** 표를 세지 않는 페이즈의 값. `needed: 1`이 "혼자면 세지 않는다"와 같은 얼굴이라 화면에 셈이 뜨지 않는다. */
const NO_SKIP_VOTES = { votes: 0, needed: 1, mine: false } as const

/* --- 조각 ------------------------------------------------------------------------ */

function publicPlayer(state: MatchState, player: MatchPlayer, viewer: PlayerId): PublicPlayer {
  const desk = state.desks?.[player.id]
  return {
    id: player.id,
    name: player.name,
    kind: player.kind,
    connected: player.connected,
    ready: player.ready,
    spectating: player.spectating,
    inOrigin: player.origin.isInOriginRoom,
    foundCount: foundBy(state, player.id).length,
    roundScore: player.roundScore,
    totalScore: player.totalScore,
    raised: player.raised,
    changeUsed: desk?.changes.length ?? 0,
    changeDone: state.phase === 'change' && deskDone(state, player),
    spectatable: canSpectate(state, viewer, player.id),
  }
}

/**
 * 원래 방 버튼의 얼굴. 방문 상태 → 얼굴은 리프(`originVisit.originStatusOf`)가 싱글과 같은 함수로 만들고, 여기는 매치의
 * 게이트만 덧댄다 - 관전 중이면 버튼 자체가 없다(`unavailable`). 수색 밖도 같다(`sessionView`와 같은 조건).
 */
function originStatusOf(state: MatchState, player: MatchPlayer, tuning: Tuning): OriginRoomStatus {
  if (state.phase !== 'hunt' || player.spectating) return { kind: 'unavailable' }
  return visitStatusOf(player.origin, tuning)
}

function selfView(state: MatchState, player: MatchPlayer, tuning: Tuning): SelfView {
  const active = isActiveHunter(state, player)
  const queueAt = state.door.queue.indexOf(player.id)
  return {
    ammo: player.ammo,
    ammoMax: ammoCapacity(tuning),
    isLowAmmo: isLowAmmo(player, tuning),
    magazines: player.magazines,
    magazineUsesLeft: magazineUsesLeft(player, tuning),
    // 네 불리언은 리듀서의 게이트를 그대로 부른다(머리말). 여기서 조건을 하나라도 다시 적으면 규칙이 두 벌이 된다.
    canUseMagazine: active && canReload(player, tuning),
    canShoot: active && !isShotBlocked(player),
    canEnterOrigin: canEnterOrigin(state, player.id),
    canStart: canStart(state, player.id),
    canChange: canUseDesk(state, player.id),
    canDecorate: canDecorate(state, player.id),
    placementsLeft: placementsLeftOf(state, player.id),
    placementsLimit: state.config.room.placementsPerPlayer,
    combo: player.combo,
    comboMultiplier: comboMultiplier(player.combo),
    comboAtCap: isComboAtCap(player.combo),
    origin: originStatusOf(state, player, tuning),
    queuePosition: queueAt < 0 ? null : queueAt + FIRST_QUEUE_POSITION,
    spectating: player.spectating,
    spectateTarget: player.spectateTarget,
    hunting: active,
    raised: player.raised,
    canRaise: active && canRaise(player, tuning),
    wager: player.wager,
    unfoundCount: unfoundOf(state, player.id).length,
  }
}

/**
 * 수색 중에는 `null`, 리빌부터 전부. 바꾸기 중에는 **내 작업대의 것**뿐이다 - 내가 만든 것은 내가 아는 것이고,
 * 남이 만든 것은 그때 아직 남의 초안에만 있다(`matchDesk`).
 */
function changesFor(state: MatchState, viewer: PlayerId): readonly MatchChange[] | null {
  if (state.phase === 'change') return state.desks?.[viewer]?.changes ?? []
  return CHANGES_VISIBLE_TO_HUNTERS.has(state.phase) ? state.changes : null
}

/**
 * **내 작업대**의 얼굴. 남의 작업대는 뷰에 자리가 없다 - 남에게 가는 것은 `PublicPlayer.changeUsed`(개수)뿐이다.
 *
 * **바꾸기 페이즈에만 있다.** 상태의 `desks`는 그 뒤(외우기)까지 남는다 - 초안을 합치는 것은 수색 진입의 일이라
 * 리듀서가 계속 들고 있어야 하기 때문이다(3절 개정으로 외우기가 바꾸기 뒤로 왔다). 그 부기가 화면까지 새면
 * 구획 진행판이 외우기 내내 서 있고(`matchSessionView.buildZoneProgressView`가 이 칸 하나로 갈린다)
 * "지금 누가 몇 개 만들고 있다"가 아무도 만들지 않는 60초 동안 떠 있게 된다.
 */
function deskFor(state: MatchState, viewer: PlayerId): DeskView | null {
  if (state.phase !== 'change') return null
  const desk = state.desks?.[viewer]
  if (desk === undefined) return null
  return {
    budget: desk.budget,
    used: desk.changes.length,
    budgetLeft: budgetLeftOf(desk),
    touched: desk.touched,
  }
}

function doorView(state: MatchState, tuning: Tuning): DoorView {
  const holder = state.door.holder === null ? undefined : playerOf(state, state.door.holder)
  return {
    holder: state.door.holder,
    holderRemainingMs: holder === undefined ? 0 : originStayRemainingMs(holder.origin, tuning),
    queue: state.door.queue.map((id) => playerOf(state, id)?.name ?? id),
    reservedFor: state.door.reservedFor,
    reservedRemainingMs: state.door.reservedRemainingMs,
    reserveTotalMs: graceMs(tuning),
  }
}

function roomsFor(state: MatchState, viewer: PlayerId): RoomsVisibility {
  if (state.phase === 'lobby') return { original: false, mutated: null }
  // 바꾸기 중에는 전원이 **자기** 초안을 본다(호스트가 받는 사람의 작업대에서 꺼낸다 - `matchHost.roomsPayload`).
  if (state.phase === 'change') return { original: true, mutated: state.desks?.[viewer] === undefined ? null : 'draft' }
  return { original: true, mutated: MUTATED_EXISTS.has(state.phase) ? 'mutated' : null }
}

/**
 * 동사별 개수. 설정이 켜져 있고 정답표가 있는 페이즈(수색부터)에만 센다. 무엇이 어디인지는 없다 - 개수만.
 *
 * **내가 찾을 수 있는 것만 센다**(M11 규칙 4). 전체를 세면 이 예고의 합이 HUD 분모(`findableCount`)보다 언제나
 * 인당 예산만큼 커서 "12개 예고, 9개 분모"가 된다. 게다가 내 구획의 세 개는 내가 이미 아는 것이라, 전체를 알려 주면
 * 뺄셈 하나로 남의 몫을 정확히 복원할 수 있다. 분자·분모·예고가 `findableChanges` 하나에서 나온다.
 */
function verbCountsFor(state: MatchState, viewer: PlayerId): Readonly<Record<Verb, number>> | null {
  if (!state.config.revealVerbCounts || !MUTATED_EXISTS.has(state.phase)) return null
  const mine = findableChanges(state, viewer)
  const counts: Record<Verb, number> = { add: 0, remove: 0, move: 0, rotate: 0, recolor: 0 }
  for (const verb of VERB_ORDER) counts[verb] = mine.filter((change) => change.verb === verb).length
  return counts
}

/**
 * 내가 찾을 수 있는 이상한 곳의 수(M11 규칙 4). 정답표가 이미 있으면(수색부터) 주인이 내가 아닌 줄을 세고,
 * 아직 없으면(외우기·바꾸기) 방 규칙에서 유도한다 - **전체 − 내 몫**이고 구획마다 정확히 예산만큼 나므로 그 뺄셈이 참이다
 * (`sessionView.buildHudView`가 목표 개수를 방 규칙에서 읽는 것과 같은 이유).
 */
function findableCount(state: MatchState, viewer: PlayerId, tuning: Tuning): number {
  if (state.changes.length > 0) return findableChanges(state, viewer).length
  // 빼는 쪽도 `budgetPerPlayer`를 지난다 - `roundTuning`이 개수에 손대는 날(그 함수의 존재 이유다) 이 뺄셈만 옛 값을 쓰면
  // HUD 분모가 실제 정답표와 어긋난다.
  return totalChangeCount(state, tuning) - budgetPerPlayer(state, tuning)
}

/** 방장 설정의 지금 값. `allowedVerbs`는 정본 순서로 편다 - `Set`의 삽입 순서가 JSON에 새면 같은 설정이 두 문자열이 된다. */
function configView(state: MatchState): MatchConfigView {
  return {
    allowedVerbs: VERB_ORDER.filter((verb) => state.config.room.allowedVerbs.has(verb)),
    changeCount: state.config.room.changeCount,
    revealVerbCounts: state.config.revealVerbCounts,
    humanDecorate: state.config.humanDecorate,
  }
}

/* --- 입구 ------------------------------------------------------------------------ */

/**
 * 한 사람이 받아도 되는 것 전부. 좌석이 없는 viewer는 상태 손상이라 던진다 - 관객 좌석(`?watch=`)은
 * 로드맵 밖이고, 그것이 생기면 여기가 아니라 별도 투영이 맡는다.
 */
export function toClientView(state: MatchState, viewer: PlayerId, deps: MatchDeps): ClientView {
  const me = playerOf(state, viewer)
  if (me === undefined) {
    throw new Error(`toClientView: 좌석 '${viewer}'가 없다 - 관객은 이 투영의 대상이 아니다`)
  }
  const tuning = deps.tuning
  const zones = matchZones(state, deps)
  return {
    me: viewer,
    roomCode: state.roomCode,
    round: state.round,
    phase: state.phase,
    phaseRemainingMs: phaseRemainingMs(state, tuning),
    hostId: state.hostId,
    players: seatsInOrder(state).map((player) => publicPlayer(state, player, viewer)),
    self: selfView(state, me, tuning),
    // 정족수도 "표를 세는 페이즈인가"도 리듀서의 함수 그대로다 - 화면이 다시 세면 그 규칙의 사본이다.
    // 앞당길 것이 없는 페이즈에서 세지 않는 이유: 로비의 감춰진 [바로 시작]이 `0/2`를 달고 있었고,
    // 그 버튼은 눌러도 아무 일이 없다(`matchPhases.onSkipVote`).
    skip: canSkipPhase(state.phase)
      ? { votes: state.skipVotes.length, needed: skipVotesNeeded(state), mine: state.skipVotes.includes(viewer) }
      : NO_SKIP_VOTES,
    changeCount: findableCount(state, viewer, tuning),
    foundCount: findableFound(state, viewer),
    zones,
    myZone: zoneOf(zones, viewer) ?? null,
    config: configView(state),
    found: state.found,
    changes: changesFor(state, viewer),
    desk: deskFor(state, viewer),
    door: doorView(state, tuning),
    rooms: roomsFor(state, viewer),
    verbCounts: verbCountsFor(state, viewer),
    ranking: state.phase === 'result' ? cumulativeRanking(state) : null,
    endReason: state.endReason,
  }
}
